/** Google reCAPTCHA Enterprise (score-based) helpers for web. */

const SCRIPT_ID = "recaptcha-enterprise-script";

interface GrecaptchaEnterprise {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: GrecaptchaEnterprise };
  }
}

export function isRecaptchaEnterpriseEnabled(siteKey: string): boolean {
  return (
    process.env.REACT_APP_ENV === "production" &&
    siteKey.length > 0 &&
    // Legacy misconfig: backend secret was used as a "site" key.
    siteKey !== "6Lf_6c0eAAAAAAlhCbNzDT7MJwe7AAShUi0SvYkO"
  );
}

function loadEnterpriseScript(siteKey: string): Promise<void> {
  if (window.grecaptcha?.enterprise) return Promise.resolve();

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load reCAPTCHA Enterprise")),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load reCAPTCHA Enterprise"));
    document.head.appendChild(script);
  });
}

/** Returns an Enterprise token for the given action (e.g. LOGIN). */
export async function executeRecaptchaEnterprise(
  siteKey: string,
  action: string,
): Promise<string> {
  await loadEnterpriseScript(siteKey);

  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) {
    throw new Error("reCAPTCHA Enterprise is not available");
  }

  await new Promise<void>((resolve) => {
    enterprise.ready(() => resolve());
  });

  const token = await enterprise.execute(siteKey, { action });
  if (!token) throw new Error("reCAPTCHA Enterprise returned an empty token");
  return token;
}
